/**
 * Lock notes & history display for the Loan Lock tab:
 *   • LockNotesBlock — the lock's `lock_notes`, below the timeline (rendered
 *     only when there are notes). One field is shared by request, confirm and
 *     Edit details, so the last save replaces the earlier text.
 *   • extensionEvents — timeline rows for pending / confirmed / denied
 *     extensions, with their reasons.
 *   • PreviousLocksCard — superseded / cancelled locks on the loan
 *     (GET /pricing/locks/{dealId}) with every note and reason they carried.
 */
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import isNil from "lodash/isNil";
import { useEffect, useMemo, useState } from "react";

import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { losLocksApi } from "../../../../api/losLocksApi";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";

/* ── helpers ───────────────────────────────────────────────────────────── */

const oidOf = (v) => (v && typeof v === "object" ? v.$oid : v) || null;

/** "Lock notes" block at the bottom of the timeline card. */
export const LockNotesBlock = ({ notes }) => (
  <Box
    sx={{
      mt: 3,
      pt: 2.25,
      borderTop: "1px solid #E2E8F0",
    }}
  >
    <Box
      sx={{
        fontSize: 11.5,
        fontWeight: 700,
        color: "#94A3B8",
        textTransform: "uppercase",
        letterSpacing: 0.7,
        mb: 1,
      }}
    >
      Lock notes
    </Box>
    {/* Gray box; the larger, heavier text makes the notes stand out. */}
    <Box
      sx={{
        fontSize: 14,
        fontWeight: 500,
        color: "#0F172A",
        lineHeight: 1.55,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        background: "#F8FAFC",
        border: "1px solid #E2E8F0",
        borderRadius: "10px",
        p: "12px 14px",
      }}
    >
      {notes}
    </Box>
    <Box sx={{ fontSize: 11.5, color: "#94A3B8", mt: 0.75 }}>
      One field shared by request, confirm and Edit details. A note added on
      confirm is appended; Edit details replaces the whole text.
    </Box>
  </Box>
);

/**
 * Timeline rows for a lock's extensions: each confirmed / denied entry in
 * `extensionHistory`, then the open `pendingExtension`. Returns descriptors the
 * tab maps onto its own <Event>.
 */
export const extensionEvents = (lock) => {
  const rows = [];
  const history = Array.isArray(lock?.extensionHistory)
    ? lock.extensionHistory
    : [];
  history.forEach((x, i) => {
    const denied = x?.status === "denied";
    const days = x?.extensionDays != null ? `${x.extensionDays} days` : null;
    rows.push({
      key: `ext-${i}`,
      tone: denied ? "rose" : "green",
      title: denied ? "Extension denied" : "Extended",
      meta: [
        denied ? x?.deniedBy : x?.confirmedBy,
        (denied ? x?.denyDateTime : x?.confirmDateTime)
          ? formatDate(denied ? x.denyDateTime : x.confirmDateTime)
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      body: [
        [
          days,
          x?.newExpiration ? `to ${formatDate(x.newExpiration)}` : null,
          !isNil(x?.fee) ? `fee ${+Number(x.fee).toFixed(2)} bps` : null,
        ]
          .filter(Boolean)
          .join(" "),
        x?.reason ? `Requested by ${x?.requestedBy || "—"}: ${x.reason}` : null,
        denied && x?.denyReason ? `Denial reason: ${x.denyReason}` : null,
      ]
        .filter(Boolean)
        .join("\n"),
    });
  });
  const p = lock?.pendingExtension;
  if (p) {
    rows.push({
      key: "ext-pending",
      tone: "amber",
      title: "Extension requested",
      meta: [
        p.requestedBy,
        p.requestDateTime ? formatDate(p.requestDateTime) : null,
      ]
        .filter(Boolean)
        .join(" · "),
      body: [
        [
          p.extensionDays != null ? `${p.extensionDays} days` : null,
          p.newExpiration ? `to ${formatDate(p.newExpiration)}` : null,
          !isNil(p.fee) ? `fee ${+Number(p.fee).toFixed(2)} bps` : null,
        ]
          .filter(Boolean)
          .join(" "),
        p.reason ? `Reason: ${p.reason}` : null,
        "Awaiting capital markets.",
      ]
        .filter(Boolean)
        .join("\n"),
    });
  }
  return rows;
};

// Every piece of free text a lock record can carry, labelled.
const lockTexts = (l) =>
  [
    l?.lock_notes ? `Notes: ${l.lock_notes}` : null,
    l?.actionNeeded?.reason ? `Returned: ${l.actionNeeded.reason}` : null,
    l?.cancellation?.reason ? `Cancelled: ${l.cancellation.reason}` : null,
    ...(Array.isArray(l?.extensionHistory) ? l.extensionHistory : []).flatMap(
      (x) => [
        x?.reason ? `Extension: ${x.reason}` : null,
        x?.denyReason ? `Extension denied: ${x.denyReason}` : null,
      ],
    ),
  ].filter(Boolean);

/**
 * "Previous locks" card — every lock on the loan except the current
 * one, with all the free text it carried. Fetched on mount; renders nothing
 * when there is no history.
 */
export const PreviousLocksCard = ({ dealId, currentLockId }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!dealId) return undefined;
    let alive = true;
    losLocksApi
      .getLocksForDeal(dealId)
      .then((data) => {
        if (alive) setRows(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (alive) setError(e?.message || "Could not load lock history.");
      });
    return () => {
      alive = false;
    };
  }, [dealId]);

  const previous = useMemo(
    () =>
      (rows || []).filter(
        (l) => !currentLockId || oidOf(l?._id) !== currentLockId,
      ),
    [rows, currentLockId],
  );

  const columnDefs = useMemo(
    () => [
      { headerName: "Status", field: "lock_status", minWidth: 110, maxWidth: 130 },
      {
        headerName: "Requested",
        minWidth: 160,
        maxWidth: 200,
        valueGetter: (p) =>
          [
            p.data?.lock_request_user?.fullName,
            p.data?.lock_requested_date
              ? formatDate(p.data.lock_requested_date)
              : null,
          ]
            .filter(Boolean)
            .join(" · ") || "—",
      },
      {
        headerName: "Notes & reasons",
        flex: 1,
        minWidth: 260,
        wrapText: true,
        autoHeight: true,
        valueGetter: (p) => lockTexts(p.data).join("\n") || "—",
        cellStyle: { whiteSpace: "pre-wrap", lineHeight: "1.45", paddingTop: 8, paddingBottom: 8 },
      },
    ],
    [],
  );

  if (error) {
    return (
      <Box sx={{ fontSize: 12.5, color: "#94A3B8" }}>
        Previous locks: {error}
      </Box>
    );
  }
  if (!rows || previous.length === 0) return null;

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          px: 3,
          pt: 2.5,
          pb: 1.5,
        }}
      >
        <Box sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>
          Previous locks
        </Box>
        <Box sx={{ fontSize: 12.5, color: "#94A3B8" }}>
          {previous.length} superseded or cancelled
        </Box>
      </Box>
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <AgGridTableWrapper height="auto" compactTable noBorder>
          <AgGridReact
            rowData={previous}
            columnDefs={columnDefs}
            getRowId={(p) => oidOf(p.data?._id) || p.data?.createDateTime}
            defaultColDef={{ sortable: true, resizable: true }}
            domLayout="autoHeight"
            suppressCellFocus
          />
        </AgGridTableWrapper>
      </Box>
    </Box>
  );
};
